import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { Route as appRoute } from '../app.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader.js';
import { TwoFactorSection } from '../../components/settings/TwoFactorSection.js';
import { ApiError, apiPost } from '../../lib/api.js';
import { useAuthStore } from '../../lib/auth.js';
import type { AuthSuccess } from '../../types/api.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  // Auth is already enforced by the /app parent's beforeLoad.
  path: '/settings',
  component: SettingsPage,
});

function SettingsPage() {
  const setSession = useAuthStore((s) => s.setSession);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);

    // Client-side checks mirror the server validation for a snappier UX.
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the current one.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiPost<AuthSuccess>('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      // The server rotated our session — adopt the fresh access token so this
      // device stays signed in (other devices are now logged out).
      setSession(result.accessToken, result.user);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl">
      <AdminPageHeader
        title="Account settings"
        description="Manage your sign-in credentials."
      />

      <section className="surface p-5 sm:p-6">
        <h2 className="font-serif text-lg text-ink tracking-tightish mb-1">Change password</h2>
        <p className="text-sm text-muted mb-5">
          For your security, you'll be signed out on all other devices.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••••"
            />
          </div>

          <div>
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••••"
            />
            <p className="text-xs text-muted mt-1.5">At least 8 characters.</p>
          </div>

          <div>
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••••"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          {done && (
            <div
              className="rounded-lg px-3 py-2 text-sm"
              style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
            >
              <strong>Password changed.</strong> You've been signed out on all other devices.
            </div>
          )}

          <Button type="submit" loading={submitting} className="mt-1">
            Change password
          </Button>
        </form>
      </section>

      <TwoFactorSection />
    </div>
  );
}
